/**
 * Hook para manejar logros
 *
 * Gestiona el estado de achievements y muestra toasts cuando se desbloquean nuevos.
 */

import { useState, useCallback, useEffect } from 'react';
import {
  loadAchievementState,
  checkAndUnlockAchievements,
  updateStats,
  trackFeatureUsed,
  type Achievement,
  type UserAchievementState,
} from '@/services/achievements';
import { useHistoryStore } from '@/store';

export function useAchievements() {
  const [achievementState, setAchievementState] = useState<UserAchievementState | null>(null);
  const [newAchievement, setNewAchievement] = useState<Achievement | null>(null);
  const getStreak = useHistoryStore((state) => state.getStreak);

  // Cargar estado inicial
  useEffect(() => {
    const state = loadAchievementState();
    setAchievementState(state);
  }, []);

  // Sincronizar stats con historial
  const syncStats = useCallback(async () => {
    const streak = await getStreak();
    const state = loadAchievementState();

    // Actualizar streak actual
    if (state.stats.currentStreak !== streak) {
      const unlocked = updateStats({ currentStreak: streak });
      if (unlocked.length > 0 && unlocked[0]) {
        setNewAchievement(unlocked[0]);
      }
      setAchievementState(loadAchievementState());
    }
  }, [getStreak]);

  // Sincronizar al montar
  useEffect(() => {
    syncStats();
  }, [syncStats]);

  // Registrar completion
  const recordCompletion = useCallback(() => {
    const state = loadAchievementState();
    const unlocked = updateStats({
      totalCompletions: state.stats.totalCompletions + 1,
    });

    if (unlocked.length > 0 && unlocked[0]) {
      setNewAchievement(unlocked[0]);
    }
    setAchievementState(loadAchievementState());
  }, []);

  // Registrar dia perfecto
  const recordPerfectDay = useCallback(() => {
    const state = loadAchievementState();
    const unlocked = updateStats({
      perfectDays: state.stats.perfectDays + 1,
    });

    if (unlocked.length > 0 && unlocked[0]) {
      setNewAchievement(unlocked[0]);
    }
    setAchievementState(loadAchievementState());
  }, []);

  // Registrar recuperacion de dia dificil
  const recordHardDayRecovery = useCallback(() => {
    const state = loadAchievementState();
    const unlocked = updateStats({
      hardDaysRecovered: state.stats.hardDaysRecovered + 1,
    });

    if (unlocked.length > 0 && unlocked[0]) {
      setNewAchievement(unlocked[0]);
    }
    setAchievementState(loadAchievementState());
  }, []);

  // Registrar uso de feature
  const recordFeatureUsed = useCallback((featureId: string) => {
    const unlocked = trackFeatureUsed(featureId);

    if (unlocked.length > 0 && unlocked[0]) {
      setNewAchievement(unlocked[0]);
    }
    setAchievementState(loadAchievementState());
  }, []);

  // Cerrar toast de achievement
  const dismissAchievement = useCallback(() => {
    setNewAchievement(null);
  }, []);

  // Verificar todos los logros pendientes
  const checkAllAchievements = useCallback(() => {
    const state = loadAchievementState();
    const unlocked = checkAndUnlockAchievements(state);

    if (unlocked.length > 0 && unlocked[0]) {
      setNewAchievement(unlocked[0]);
    }
    setAchievementState(loadAchievementState());
  }, []);

  return {
    achievementState,
    newAchievement,
    recordCompletion,
    recordPerfectDay,
    recordHardDayRecovery,
    recordFeatureUsed,
    dismissAchievement,
    checkAllAchievements,
    syncStats,
  };
}
